/**
 * 14 · IRR metrics (`engine.irrSnapshot@2`, 05.5): EVE for ±200bp as a share of equity (E1, the `mtm` port's `eve`), of which `eve200Rate` is the worse
 * side (E3: the risk measure and the share-price premium read the worse of a rise and a fall), and S, the 12-month NII change for +100bp on a
 * static balance sheet (E2; signed: the rate position's target and the displayed sensitivity), from the posted month-end state.
 *   05.5 E2  S = [A − L] · shock ÷ (12 · NII(m)),  A = w_float·(floating assets + pay-fixed notional) + w_runoff·R12,
 *            L = Σ balance·β̄·ρ(h)·w (deposits within their horizon) + w·wholesale + w_float·receive-fixed notional
 *            ρ(h) = (1 ÷ 12)·Σ_{j=1..12} [1 − (1 − h)^j];  β̄ = the mean of β over the move (01.3 E1); on a fall h × `downAsymmetry` and the floors
 *   05.5 E1  retail checking and retail money market durations scale with the rate level (`eveProducts`)
 * R12 is the 12-month principal of the fixed assets at the base CPR (05.1): a pool's from the level-pay schedule, a bullet's whole
 * balance when it matures within 12 months.
 */
import { ASSET_BOOKS, InvariantViolation, LIABILITY_BOOKS, HTM_BOOKS, AFS_BOOKS, smmOfCpr, type BankState, type BookId, type EngineContext, type IrrSnapshot, type DepositBetaParams, type ModelCall, type ProductDef, type Rate, type SwapPosition, type Vintage } from "@bsim/domain";
import { betaOverMove, frontBookRules, frontLotRateRisk } from "@bsim/models";
import { bankRef, optionalRef, type ArchetypeInputs } from "../archetype-inputs";
import { lastKeyLookup } from "../last-key-lookup";
import { resolveModel } from "../resolve-model";
import { PNL } from "../run/layout";
import { callOf, snapshotMonth, type Step } from "../run/run-state";

const FLOATING_ASSET = 1, FIXED_ASSET = 2, LIABILITY = 3, LIABILITY_WITHIN_YEAR = 4;
const SECURITIES: readonly BookId[] = [...AFS_BOOKS, ...HTM_BOOKS];

/** Per product: how S treats it, its weight (liabilities), and the share of a pool left after 12 months of its base SMM (pools). Built once per archetype. */
export interface Table { readonly kind: Uint8Array; readonly weight: Float64Array; readonly frontLotWeight: Float64Array; readonly floorRate: Float64Array; readonly downRatio: Float64Array; readonly beta: readonly (DepositBetaParams | undefined)[]; readonly retained12: Float64Array; readonly repricing: Readonly<Record<string, number>>; readonly niiShockBp: number; readonly eveShockBp: number }
const tables = new WeakMap<ArchetypeInputs, Table>();
/** The ratio of a deposit's mean beta over the shock to its mid-band (1 without `logistic`), per product, for one (fed funds, shock); recomputed when either changes. */
const ratioCache = new WeakMap<Table, { ff: number; shockBp: number; ratio: Float64Array }>();

/** ρ(h): the average share of a rate move realized over the next 12 months by a partial-adjustment liability. */
const realization = (h: number): number => (h === 0 ? 0 : 1 - ((1 - h) * (1 - (1 - h) ** 12)) / (h * 12));

export function tableOf(inputs: ArchetypeInputs, ctx: EngineContext): Table {
  let t = tables.get(inputs);
  if (t !== undefined) return t;
  const irr = ctx.content.params.irr;
  if (irr === undefined) throw new InvariantViolation("PENDING", "ContentParams.irr is not in the content pack");
  const w = irr.repricingWeights;
  const n = inputs.products.length;
  const table = { kind: new Uint8Array(n), weight: new Float64Array(n), frontLotWeight: new Float64Array(n), floorRate: new Float64Array(n).fill(-Infinity), downRatio: new Float64Array(n).fill(1), beta: new Array<DepositBetaParams | undefined>(n).fill(undefined), retained12: new Float64Array(n), repricing: w, niiShockBp: irr.niiShockBp, eveShockBp: irr.eveShockBp };
  inputs.products.forEach((p, i) => {
    const refs = inputs.refs[i];
    if ((ASSET_BOOKS as readonly BookId[]).includes(p.bookId)) table.kind[i] = p.rateType === "floating" ? FLOATING_ASSET : FIXED_ASSET;
    else if (refs?.depositBeta !== undefined) {
      const { betaThroughCycle, adjustmentSpeedPerMonth, downAsymmetry, floorRate, logistic } = refs.depositBeta.params;
      table.floorRate[i] = floorRate;
      table.downRatio[i] = downAsymmetry === undefined ? 1 : realization(adjustmentSpeedPerMonth * downAsymmetry) / realization(adjustmentSpeedPerMonth); // 01.3 E2: h × a on a fall
      if (logistic !== undefined) (table.beta as (DepositBetaParams | undefined)[])[i] = refs.depositBeta.params;
      const classWeight = p.nmdAverageLifeYears !== undefined ? 1 : (w[p.bookId === "time" ? "timeDepositsWithin12m" : p.bookId] ?? 1);
      table.kind[i] = p.nmdAverageLifeYears !== undefined ? LIABILITY : LIABILITY_WITHIN_YEAR;
      table.weight[i] = betaThroughCycle * realization(adjustmentSpeedPerMonth) * classWeight;
      const flow = optionalRef(p, "depositFlow"), rules = flow === undefined ? undefined : frontBookRules(flow.params, p.id);
      table.frontLotWeight[i] = rules?.frontLots === true ? frontLotRateRisk(rules).beta * realization(adjustmentSpeedPerMonth) * classWeight : table.weight[i] as number; // 02.5, T16: β of a live front lot
    } else if (p.bookId === "fhlb" || p.bookId === "ffp" || p.bookId === "dw") {
      table.kind[i] = p.rateType === "floating" ? LIABILITY : LIABILITY_WITHIN_YEAR;
      table.weight[i] = w[p.rateType === "floating" ? "fhlbShort" : "fhlbTermWithin12m"] ?? 1;
    }
    const prepay = refs?.prepay;
    if (prepay !== undefined) table.retained12[i] = (1 - smmOfCpr(prepay.params.baseCprRate)) ** 12; // 05.1 E2 at the base CPR: the share a pool keeps after 12 months of it
  });
  tables.set(inputs, (t = table));
  return t;
}

/** The 12-month principal of a level-pay pool with `monthsLeft` payments left: B·(1 − (1 − SMM)^12·F₁₂), F₁₂ the scheduled balance fraction after 12 payments (`retained12` = (1 − SMM)^12). */
function poolPrincipal12(balanceUsd: number, wacRate: number, monthsLeft: number, retained12: number): number {
  if (monthsLeft <= 12) return balanceUsd;
  const i = wacRate / 12;
  const scheduledLeft = i > 0 ? (1 - (1 + i) ** -(monthsLeft - 12)) / (1 - (1 + i) ** -monthsLeft) : (monthsLeft - 12) / monthsLeft;
  return balanceUsd * (1 - retained12 * scheduledLeft);
}

/**
 * E2's 12-month NII change for a parallel `shockBp` on the posted state (with `swaps`' overlay: the bank's own, or none for S_nat, 05.4 E5). On the way down a deposit's rate cannot fall below its floor (01.3 E3), so each
 * vintage's pass-through is capped at its room above the floor and −100/−200 are not mirror images of +100/+200 when a floor binds.
 */
export function niiChangeUsd(bank: BankState, inputs: ArchetypeInputs, table: Table, month: number, ffRate: number, shockBp: number, swaps: readonly SwapPosition[] = bank.swaps): number {
  const w = table.repricing, level = levelRatios(table, ffRate, shockBp), down = shockBp < 0;
  let floating = 0, runoff = 0, liabilities = 0;
  const indexOf = lastKeyLookup(inputs.productIndex);
  const index = (v: Vintage): number => indexOf(v.productId) as number;
  for (const book of ASSET_BOOKS) {
    const carried = SECURITIES.includes(book);
    for (const v of bank.books[book].vintages) {
      const pi = index(v);
      if (table.kind[pi] === FLOATING_ASSET) floating += v.balanceUsd;
      else if (table.kind[pi] === FIXED_ASSET && v.maturityMonth !== undefined && v.balanceUsd > 0) {
        const monthsLeft = v.maturityMonth - month;
        const principal = inputs.refs[pi]?.prepay !== undefined ? poolPrincipal12(v.balanceUsd, v.wacCouponRate ?? v.couponRate, monthsLeft, table.retained12[pi] as number) : monthsLeft <= 12 && monthsLeft > 0 ? v.balanceUsd : 0;
        runoff += carried ? (principal * (v.fairValueUsd ?? v.balanceUsd)) / v.balanceUsd : principal;
      }
    }
  }
  for (const book of LIABILITY_BOOKS) {
    for (const v of bank.books[book].vintages) {
      const pi = index(v), kind = table.kind[pi];
      if (kind === LIABILITY || (kind === LIABILITY_WITHIN_YEAR && v.maturityMonth !== undefined && v.maturityMonth - month <= 12)) {
        const live = v.frontBook !== undefined && v.frontBook.graduatesMonth > v.originMonth && v.frontBook.graduatesMonth > month; // a front lot until it graduates
        const weight = (live ? (table.frontLotWeight[pi] as number) : (table.weight[pi] as number) * (level[pi] as number)) * (down ? (table.downRatio[pi] as number) : 1);
        liabilities += v.balanceUsd * (shockBp < 0 ? Math.min(weight, Math.max(0, v.couponRate - (table.floorRate[pi] as number)) / (-shockBp / 10_000)) : weight);
      }
    }
  }
  for (const s of swaps) { if (s.direction === "payFixed") floating += s.notionalUsd; else liabilities += s.notionalUsd * (w["floating"] as number); }
  return (((w["floating"] as number) * floating + (w["fixedRunoff12m"] as number) * runoff - liabilities) * shockBp) / 10_000;
}

function levelRatios(table: Table, ffRate: number, shockBp: number): Float64Array {
  let c = ratioCache.get(table);
  if (c === undefined) ratioCache.set(table, (c = { ff: NaN, shockBp: NaN, ratio: new Float64Array(table.kind.length).fill(1) }));
  if (c.ff !== ffRate || c.shockBp !== shockBp) {
    table.beta.forEach((p, i) => { if (p !== undefined) c.ratio[i] = betaOverMove(p, ffRate, shockBp / 10_000) / p.betaThroughCycle; });
    c.ff = ffRate; c.shockBp = shockBp;
  }
  return c.ratio;
}

/**
 * 05.5 E1: the products with each retail checking / money market duration scaled for a parallel `shockBp` at fed funds `ffRate`, D·(1 − β̄(ffRate, shock)) ÷ (1 − β̄(ffAtStart, shock)),
 * β̄ the mean of the 01.3 E1 β over the move: the SimStart reading is the pinned one (I22) and a bank at higher rates has shorter deposits. The products unchanged are returned as they are.
 */
export function eveProducts(inputs: ArchetypeInputs, ffRate: number, ffAtStart: number, shockBp: number): readonly ProductDef[] {
  if (ffRate === ffAtStart) return inputs.products;
  const shock = shockBp / 10_000;
  let scaled = false;
  const products = inputs.products.map((p, i) => {
    const beta = inputs.refs[i]?.depositBeta?.params;
    if (beta?.logistic === undefined || p.eve === undefined) return p;
    const scale = (1 - betaOverMove(beta, ffRate, shock)) / (1 - betaOverMove(beta, ffAtStart, shock));
    if (scale === 1) return p;
    scaled = true;
    return { ...p, eve: { ...p.eve, durationYears: p.eve.durationYears * scale } };
  });
  return scaled ? products : inputs.products;
}

/** E3: S⁻, the 12-month NII change for −`niiShockBp` over NII, and S_worse = min(S⁺, S⁻) (signed; the displayed S stays S⁺). With no positive NII the last S holds. */
function worseSensitivityRate(bank: BankState, inputs: ArchetypeInputs, ctx: EngineContext, month: number, ffRate: number, annualNiiUsd: number, plus: Rate): Rate {
  if (annualNiiUsd <= 0) return bank.irr.niiSensitivityWorseRate ?? plus;
  const table = tableOf(inputs, ctx);
  return Math.min(plus, niiChangeUsd(bank, inputs, table, month, ffRate, -table.niiShockBp) / annualNiiUsd) as Rate;
}

/** E2 on `bank` at `month`; with no positive NII the last S holds. */
function sensitivityRate(bank: BankState, inputs: ArchetypeInputs, ctx: EngineContext, month: number, ffRate: number, annualNiiUsd: number): Rate {
  if (annualNiiUsd <= 0) return bank.irr.niiSensitivityRate;
  const table = tableOf(inputs, ctx);
  return (niiChangeUsd(bank, inputs, table, month, ffRate, table.niiShockBp) / annualNiiUsd) as Rate;
}

/** 05.5: EVE and S of `bank` at the month of `call` (a call on the bank-level `mtm` ref, which carries the swap spread). */
/** 05.5 E1: the change in EVE as a share of equity for a +200bp (`up`) and a −200bp (`down`) move; `irrSnapshot` keeps the worse of the two. */
export function eveSides(bank: BankState, inputs: ArchetypeInputs, ctx: EngineContext, call: ModelCall<"mtm">): { readonly up: Rate; readonly down: Rate } {
  const { eveShockBp } = tableOf(inputs, ctx), mtm = resolveModel(ctx.registry, call.ref);
  const eveAt = (shockBp: number): Rate => mtm.eve(call, { bank, products: eveProducts(inputs, call.env.ffRate, call.baselineEnv.ffRate, shockBp), shockBp }).eveChangeRate;
  return { up: eveAt(eveShockBp), down: eveAt(-eveShockBp) };
}

export function irrSnapshot(bank: BankState, inputs: ArchetypeInputs, ctx: EngineContext, call: ModelCall<"mtm">, annualNiiUsd: number, read: { sides?: { readonly up: Rate; readonly down: Rate } } = {}): IrrSnapshot {
  // `eve200Rate` carries the worse side, so the examiner and the premium (which read this one field) read 05.5 E3 without a CP-1 change. `read.sides` gives the two sides back to a caller that wants them (the run step), from the same call.
  const sides = eveSides(bank, inputs, ctx, call);
  read.sides = sides;
  const eveChangeRate = Math.min(sides.up, sides.down) as Rate;
  const e = bank.equity;
  const niiSensitivityRate = sensitivityRate(bank, inputs, ctx, call.month, call.env.ffRate, annualNiiUsd);
  return {
    eve200Rate: eveChangeRate, eve200EquityBasisUsd: (e.commonApicUsd + e.retainedEarningsUsd + e.aociAfsUsd + e.aociSwapUsd) as IrrSnapshot["eve200EquityBasisUsd"],
    niiSensitivityRate, niiSensitivityWorseRate: worseSensitivityRate(bank, inputs, ctx, call.month, call.env.ffRate, annualNiiUsd, niiSensitivityRate),
  };
}

/** 14 · S every month (it carries forward when NII ≤ 0); EVE, a pure read of the state, only in a month whose snapshots are read. */
export const irr: Step = (run) => {
  const call = callOf(run, bankRef(run.inputs, "mtm"));
  const read = snapshotMonth(run);
  for (const seat of run.seats) {
    const annualNiiUsd = 12 * ((seat.pnl[PNL.interestIncome] as number) - (seat.pnl[PNL.interestExpense] as number));
    if (read) { const got: { sides?: { readonly up: Rate; readonly down: Rate } } = {}; seat.bank.irr = irrSnapshot(seat.bank, run.inputs, run.ctx, call, annualNiiUsd, got); seat.eveSides = got.sides; continue; }
    const { niiSensitivityWorseRate: _stale, ...held } = seat.bank.irr; // the worse side is read with the snapshots; between them S⁺ stands for it
    seat.bank.irr = { ...held, niiSensitivityRate: sensitivityRate(seat.bank, run.inputs, run.ctx, run.month, run.env.ffRate, annualNiiUsd) };
  }
};
